/*
 * This file MUST NOT include imports or heavy logic.
 * It is injected at the very top of the header to load as quickly as possible.
 */

"use strict";

globalThis.ThemeBasicBlock = class ThemeBasicBlock {
  _domReady = false;

  constructor() {
    this._setupInstance();
  }

  _setupInstance() {
    const ready = () => {
        this._initializeAllBlocks(this._getBlocks(), !!window.acf);
        this._domReady = true;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ready, { once: true });
    } else {
      // Let the derived constructor initialize its private fields first.
      queueMicrotask(ready);
    }
  }

  _initializeAllBlocks(blocks, admin) {
    if (!blocks || blocks.length === 0) {
      return;
    }

    blocks.forEach((block) => this._scheduleBlock(block, admin));
  }

  /**
   * Decide how one block gets initialised. Single source of truth: the same
   * path must serve blocks known at DOMContentLoaded and blocks that register
   * later, or late registrations silently lose the editor re-render hook.
   *
   * In the editor the ACF preview is re-rendered on every field change, so the
   * block has to re-initialise on `render_block_preview`. It must ALSO be
   * initialised once up front: with block API v3 the canvas is an iframe and the
   * block's `script.js` is loaded inside it, which happens AFTER the preview it
   * belongs to is already in the DOM — waiting for the next render would leave
   * that first preview dead. Running both is safe because `_initializeBlock()`
   * destroys the previous instances before creating new ones.
   */
  _scheduleBlock(block, admin) {
    if (admin && window.acf) {
      window.acf.addAction(`render_block_preview/type=${block.name}`, () => {
        this._initializeBlock(block);
      });
    }

    this._initializeBlock(block);
  }

  /**
   * Query the top document and every same-origin iframe in it. The block editor
   * canvas is an iframe (always under block API v3), so a plain
   * `document.querySelectorAll` from the top window sees no block markup at all.
   */
  _queryAllDeep(selector) {
    const found = [...document.querySelectorAll(selector)];

    document.querySelectorAll("iframe").forEach((frame) => {
      try {
        const doc = frame.contentDocument;
        if (doc) found.push(...doc.querySelectorAll(selector));
      } catch (e) {
        /* cross-origin frame — nothing to initialise there */
      }
    });

    return found;
  }

  _getScope(block) {
    // `block.selector` overrides the derived class for blocks whose normalized
    // name collides with an existing utility class (theme/accordion →
    // `.theme-accordion`, which is a header CSS helper).
    return this._queryAllDeep(
      block.selector || `.${this._normalizeBlockName(block.name)}`
    );
  }

  _normalizeBlockName(name) {
    return name?.replace(/[^a-z0-9-_]+/g, "-").toLowerCase();
  }

  _getBlocks() {
    return [];
  }
  _initializeBlock(block) {
    console.error("Must override");
  }
  _isValidBlock(block) {
    console.error("Must override");
  }
  _isBlockExist(block) {
    return this._getBlocks().some((item) => item.name === block.name);
  }
};

class ThemeBlocks extends ThemeBasicBlock {
  static #instance;
  #blocks = [];
  // name -> array of handler instances, so a re-init (ACF editor re-render)
  // can tear the previous instances down before creating fresh ones.
  #instances = new Map();
  constructor() {
    super();
    if (ThemeBlocks.#instance) return ThemeBlocks.#instance;
    ThemeBlocks.#instance = this;
  }

  _getBlocks() {
    return this.#blocks;
  }

  _initializeBlock(block) {
    // Destroy any instances from a previous render of this block type.
    const previous = this.#instances.get(block.name);
    if (previous) {
      previous.forEach((instance) => {
        try {
          if (instance && typeof instance.destroy === "function") {
            instance.destroy();
          }
        } catch (error) {
          console.error("Block destroy error:", block, "\nReason:", error);
        }
      });
    }

    const created = [];
    try {
      this._getScope(block).forEach((scope) => {
        const instance = this._isClass(block.handler)
          ? new block.handler(scope, block.params)
          : block.handler(scope, block.params);
        if (instance) {
          created.push(instance);
        }
      });
    } catch (error) {
      console.error("Block init error:", block, "\nReason:", error);
    }
    this.#instances.set(block.name, created);
  }

  _isValidBlock(block) {
    return (
      block &&
      typeof block === "object" &&
      typeof block.name === "string" &&
      typeof block.handler === "function"
    );
  }

  _isClass(fn) {
    return typeof fn === "function" && /^\s*class\s+/.test(fn.toString());
  }

  add(block) {
    if (!this._isValidBlock(block)) {
      console.error("Invalid block:", block);
      return;
    }
    if (this._isBlockExist(block)) return;
    this.#blocks.push(block);

    if (this._domReady) {
      this._scheduleBlock(block, !!window.acf);
    }
  }
}

globalThis.ThemeBlocks = globalThis.ThemeBlocks || new ThemeBlocks();
